/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import { useHotkeys } from '@tanstack/react-hotkeys';
import { appHotkeys } from '../desktop/hotkeys/appHotkeys';
import { useVoiceShortcut } from './voice/useVoiceShortcut';

interface AgentShortcutActions {
  isAgentPanelOpen: boolean;
  startVoiceShortcut: () => void;
  endVoiceShortcut: (cancelled: boolean) => void;
  tapVoiceShortcut: () => boolean;
  focusAgentInput: () => void;
  openCommandPalette: () => void;
  openNewAgentConversation: () => void;
  toggleHistory: () => void;
  focusHistorySearch: () => void;
  toggleAgentPanel: () => void;
}

/**
 * The Agent's shortcuts, on its modifier (⌃ on macOS, Alt elsewhere; see `appHotkeys`):
 *  - Space: focus the chat input if the column is open, else open Quick Find (the one search box
 *    for apps, projects and the Agent).
 *  - A: show or hide the column, from anywhere.
 *  - N, F, H (column open only): new conversation, search history, toggle history.
 * W is left to apps: it closes the focused window or tab.
 */
export function useAgentShortcuts({
  isAgentPanelOpen,
  startVoiceShortcut,
  tapVoiceShortcut,
  endVoiceShortcut,
  focusAgentInput,
  openCommandPalette,
  openNewAgentConversation,
  toggleHistory,
  focusHistorySearch,
  toggleAgentPanel,
}: AgentShortcutActions) {
  const startFocusGesture = useVoiceShortcut({
    onTap: () => {
      if (tapVoiceShortcut()) return;
      if (isAgentPanelOpen) focusAgentInput();
      else openCommandPalette();
    },
    onHoldStart: startVoiceShortcut,
    onHoldEnd: endVoiceShortcut,
  });
  const whileOpen = { enabled: isAgentPanelOpen };

  useHotkeys([
    {
      hotkey: appHotkeys['agent.focus'],
      callback: startFocusGesture,
    },
    { hotkey: appHotkeys['agent.toggle'], callback: toggleAgentPanel },
    {
      hotkey: appHotkeys['agent.newConversation'],
      callback: () => {
        openNewAgentConversation();
        focusAgentInput();
      },
      options: whileOpen,
    },
    { hotkey: appHotkeys['agent.searchHistory'], callback: focusHistorySearch, options: whileOpen },
    { hotkey: appHotkeys['agent.toggleHistory'], callback: toggleHistory, options: whileOpen },
  ]);
}
